import {
  RedoButton,
  RedoButtonProps,
} from "@redotech/arbiter/buttons/redo-button";
import { copyToClipboard } from "@redotech/arbiter/clipboard";
import CopyIcon from "@redotech/redo-icons/copy-01.svg";
import { sinkPromise } from "@redotech/util/promise";
import { memo, MouseEvent, useEffect, useRef, useState } from "react";
import { CopyableTooltip } from "./copyable-tooltip";

const COPIED_FEEDBACK_DURATION_MS = 3000;

/**
 * Icon-only copy-to-clipboard button. Shows a "Click to copy ..." tooltip that
 * flips to "Copied!" for a few seconds after a click. Use when you want just the
 * copy affordance without rendering the copied text — for the text + icon
 * variant, use {@link CopyableText} with `showIcon`.
 */
export const CopyIconButton = memo(function CopyIconButton({
  "data-dd-action-name": actionName,
  textToCopy,
  /** Label shown in the tooltip; defaults to the copied text. */
  tooltipLabel,
  preventEventPropagation = false,
  size = "xs",
}: {
  "data-dd-action-name"?: string;
  textToCopy: string;
  tooltipLabel?: string;
  preventEventPropagation?: boolean;
  size?: RedoButtonProps["size"];
}) {
  const idleTooltip = `Click to copy ${tooltipLabel ?? textToCopy}`;
  const [copied, setCopied] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    setCopied(false);
  }, [textToCopy]);

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [],
  );

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (preventEventPropagation) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (copied) {
      return;
    }
    setCopied(true);
    sinkPromise(copyToClipboard(textToCopy));
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(
      () => setCopied(false),
      COPIED_FEEDBACK_DURATION_MS,
    );
  };

  return (
    <CopyableTooltip textToCopy={copied ? "Copied!" : idleTooltip}>
      <RedoButton
        aria-label="Copy"
        data-dd-action-name={actionName}
        IconLeading={CopyIcon}
        onClick={handleClick}
        size={size}
      />
    </CopyableTooltip>
  );
});
